<script setup lang="ts">
/**
 * PortalSection — Resident portal settings.
 *
 * Four BaseToggle rows that fire PATCH /v1/settings/portal immediately on change.
 * No save button — optimistic update with rollback on error.
 */
import { computed } from 'vue';
import BaseCard from '@/components/ui/BaseCard.vue';
import { useSettingsStore } from '@/stores/settings';
import type { HoaSettingsPortalDto } from '@/api/settings';

const store = useSettingsStore();

const portalUrl = computed(() => store.profile?.portalUrl ?? '');

const toggleRows: { field: keyof HoaSettingsPortalDto; title: string; desc: string }[] = [
  {
    field: 'allowAnonymous',
    title: 'Allow anonymous submissions',
    desc: 'Residents can submit without entering name or email.',
  },
  {
    field: 'photoUploads',
    title: 'Photo uploads',
    desc: 'Allow residents to attach photos and short videos.',
  },
  {
    field: 'autoConfirmEmail',
    title: 'Auto-confirmation email',
    desc: 'Send "we received your issue" reply when an email was provided.',
  },
  {
    field: 'embedEnabled',
    title: 'Show on website',
    desc: 'Embed snippet to display the portal on your existing HOA website.',
  },
];

const saveErrors = ref<Record<string, string>>({});

async function onToggle(field: keyof HoaSettingsPortalDto, value: boolean) {
  try {
    delete saveErrors.value[field];
    await store.savePortalToggle(field, value);
  } catch (e) {
    saveErrors.value[field] = e instanceof Error ? e.message : 'Failed to update.';
  }
}

function copyPortalUrl() {
  void navigator.clipboard.writeText(portalUrl.value);
}

import { ref } from 'vue';
</script>

<template>
  <BaseCard id="portal" padding="none" data-testid="portal-section">
    <div class="p-4 sm:p-6 border-b border-border">
      <h3 class="text-base font-semibold text-fg">Resident portal</h3>
      <p class="text-sm text-muted mt-0.5">Public submission form your homeowners use to report issues.</p>
    </div>

    <div class="p-4 sm:p-6 space-y-0">
      <!-- Portal URL -->
      <div class="pb-4 mb-4 border-b border-border space-y-1">
        <label class="block text-sm font-medium text-fg">Portal URL</label>
        <div class="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            :value="portalUrl"
            readonly
            class="flex-1 min-w-0 h-9 px-3 rounded-md border border-border bg-canvas-soft text-sm font-mono text-fg-soft"
            data-testid="portal-url-input"
          >
          <button
            class="h-9 px-3 rounded-md border border-border bg-surface text-sm font-medium text-fg hover:bg-canvas-soft transition-colors"
            data-testid="portal-url-copy-btn"
            @click="copyPortalUrl"
          >
            Copy
          </button>
          <a
            :href="portalUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="h-9 px-3 rounded-md border border-border bg-surface text-sm font-medium text-fg hover:bg-canvas-soft transition-colors flex items-center gap-1.5"
            data-testid="portal-url-open-btn"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5" aria-hidden="true">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <path d="M15 3h6v6" />
              <path d="M10 14L21 3" />
            </svg>
            Open
          </a>
        </div>
      </div>

      <!-- Toggle rows -->
      <div
        v-for="(row, i) in toggleRows"
        :key="row.field"
        class="flex items-center justify-between gap-4 py-3"
        :class="i < toggleRows.length - 1 ? 'border-b border-border' : ''"
      >
        <div class="grow min-w-0">
          <div class="text-sm font-medium text-fg">{{ row.title }}</div>
          <div class="text-xs text-muted mt-0.5">{{ row.desc }}</div>
          <p v-if="saveErrors[row.field]" class="text-xs text-red-600 mt-1">{{ saveErrors[row.field] }}</p>
        </div>

        <!-- Toggle switch -->
        <button
          role="switch"
          :aria-checked="store.portal?.[row.field] ?? false"
          :data-testid="`portal-toggle-${row.field}`"
          class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-600"
          :class="store.portal?.[row.field] ? 'bg-navy-800' : 'bg-gray-300'"
          :disabled="store.isSaving"
          @click="onToggle(row.field, !(store.portal?.[row.field] ?? false))"
        >
          <span
            class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform"
            :class="store.portal?.[row.field] ? 'translate-x-4' : 'translate-x-0'"
          />
        </button>
      </div>
    </div>
  </BaseCard>
</template>
